import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertMatchSchema, type Player, type InsertMatch, type MatchWithPlayers, type ClubWithMembership, type User } from "../../../shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

import { useRef } from "react";
import { Play, Star, Trophy } from "lucide-react";
import { SearchableSelect, type SearchableSelectRef } from "@/components/searchable-select";

interface CreateMatchFormProps {
  playerId: string | null;
  onSuccess?: () => void;
}

import { useTranslation } from "react-i18next";

export default function CreateMatchForm({ playerId, onSuccess }: CreateMatchFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get current user to check if viewer
  const { data: user } = useQuery<User>({
    queryKey: ["/api/auth/user"],
  });
  const isViewer = user?.email === 'viewer@padelo.app';

  // Get followed players from localStorage (user-specific)
  const followKey = playerId ? `followedPlayers_${playerId}` : "followedPlayers";
  const followedPlayers = new Set(JSON.parse(localStorage.getItem(followKey) || "[]"));

  const { data: players = [], isLoading: playersLoading } = useQuery<Player[]>({
    queryKey: ["/api/players"],
  });

  const { data: activeMatches = [] } = useQuery<MatchWithPlayers[]>({
    queryKey: ["/api/matches/active"],
  });

  const { data: clubs = [] } = useQuery<ClubWithMembership[]>({
    queryKey: ["/api/clubs", playerId],
    queryFn: async () => {
      const url = `/api/clubs${playerId ? `?playerId=${playerId}&_t=${Date.now()}` : ''}`;
      const response = await apiRequest("GET", url);
      const data = await response.json();
      return data;
    },
    staleTime: 0, // No cache - always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  // Filter and sort clubs - only show clubs where user is a member AND status is active
  const memberClubs = clubs
    .filter(club => club.isMember && club.status !== 'pending')
    .sort((a, b) => a.name.localeCompare(b.name));

  const form = useForm<InsertMatch>({
    resolver: zodResolver(insertMatchSchema),
    defaultValues: {
      matchType: "single_set_6",
      clubId: localStorage.getItem("lastClubId") || null,
    },
  });



  // Refs for auto-advancing fields
  const teamAPlayer1Ref = useRef<SearchableSelectRef>(null);
  const teamAPlayer2Ref = useRef<SearchableSelectRef>(null);
  const teamBPlayer1Ref = useRef<SearchableSelectRef>(null);
  const teamBPlayer2Ref = useRef<SearchableSelectRef>(null);
  const startButtonRef = useRef<HTMLButtonElement>(null);
  const [matchTypeOpen, setMatchTypeOpen] = useState(false);

  // Watch form values to filter out selected players
  const watchedValues = form.watch();
  const selectedPlayerIds = [
    watchedValues.teamAPlayer1Id,
    watchedValues.teamAPlayer2Id,
    watchedValues.teamBPlayer1Id,
    watchedValues.teamBPlayer2Id
  ].filter(Boolean);

  // Check if all conditions are met to start match
  const isReadyToStart = selectedPlayerIds.length === 4 && watchedValues.matchType;

  // Get club members if a club is selected
  const selectedClubId = watchedValues.clubId;
  const { data: clubMembers = [] } = useQuery<Player[]>({
    queryKey: ["/api/clubs", selectedClubId, "members"],
    enabled: !!selectedClubId && selectedClubId !== "no_club",
  });

  // Get players who are currently in truly active matches (not completed)
  const activePlayers = new Set<string>();
  activeMatches.forEach(match => {
    // Only exclude players from matches that are active but not completed
    // Allow players from completed matches (pending validation) to join new games
    if (match.isActive && !match.isCompleted) {
      activePlayers.add(match.teamAPlayer1Id);
      activePlayers.add(match.teamAPlayer2Id);
      activePlayers.add(match.teamBPlayer1Id);
      activePlayers.add(match.teamBPlayer2Id);
    }
  });

  const getAvailablePlayers = (currentFieldId?: string) => {
    // Use club members if a club is selected, otherwise use all players
    const playerPool = (selectedClubId && selectedClubId !== "no_club") ? clubMembers : players;

    return playerPool
      .filter(player => {
        // Allow if it's the currently selected player
        if (player.id === currentFieldId) return true;

        // Exclude if already selected in this form
        if (selectedPlayerIds.includes(player.id)) return false;

        // Exclude if already playing in an active match
        if (activePlayers.has(player.id)) return false;

        return true;
      })
      .sort((a, b) => {
        // First, sort by followed status (followed players first)
        const aFollowed = followedPlayers.has(a.id);
        const bFollowed = followedPlayers.has(b.id);

        if (aFollowed && !bFollowed) return -1;
        if (!aFollowed && bFollowed) return 1;

        // Then sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
  };

  const createMatchMutation = useMutation({
    mutationFn: async (data: InsertMatch) => {
      const response = await apiRequest("POST", "/api/matches", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: t('matches.create.success'),
      });
      form.reset({
        matchType: "single_set_6",
        teamAPlayer1Id: "",
        teamAPlayer2Id: "",
        teamBPlayer1Id: "",
        teamBPlayer2Id: "",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/matches/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches"] });

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: InsertMatch) => {
    // Validate that all players are different
    const playerIds = [data.teamAPlayer1Id, data.teamAPlayer2Id, data.teamBPlayer1Id, data.teamBPlayer2Id];
    const uniquePlayerIds = new Set(playerIds);

    if (uniquePlayerIds.size !== 4) {
      toast({
        title: "Error",
        description: t('matches.create.errorPlayers'),
        variant: "destructive",
      });
      return;
    }

    // Save user preferences to localStorage
    if (data.clubId) {
      localStorage.setItem("lastClubId", data.clubId);
    }

    createMatchMutation.mutate(data);
  };

  if (playersLoading) {
    return (
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
        <div className="text-center text-muted-foreground">{t('matches.create.loading')}</div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm" data-testid="create-match-form">


      <Form {...form}>
        <form id="create-match-form" onSubmit={form.handleSubmit(onSubmit)} className="p-4 space-y-4">
          {/* Club Selection */}
          <FormField
            control={form.control}
            name="clubId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('matches.create.club')}</FormLabel>
                <Select onValueChange={(value) => {
                  field.onChange(value === "no_club" ? null : value);
                  // Clear all player selections when club changes
                  form.setValue("teamAPlayer1Id", "");
                  form.setValue("teamAPlayer2Id", "");
                  form.setValue("teamBPlayer1Id", "");
                  form.setValue("teamBPlayer2Id", "");

                  // Auto-advance to first player selection
                  setTimeout(() => teamAPlayer1Ref.current?.open(), 100);
                }} value={field.value || "no_club"}>
                  <FormControl>
                    <SelectTrigger data-testid="select-club">
                      <SelectValue placeholder={t('matches.create.selectClub')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="no_club">{t('matches.create.noClub')}</SelectItem>
                    {memberClubs.map(club => (
                      <SelectItem key={club.id} value={club.id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{club.name}</span>
                          <img src="/tennis-ball.png" alt="Club" className="w-3.5 h-3.5 ml-2 dark:invert" />
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Show message if club selected but no members */}
          {selectedClubId && selectedClubId !== "no_club" && clubMembers.length === 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                {t('matches.create.noMembers')}
              </p>
            </div>
          )}

          {/* Team A Selection */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">{t('matches.create.teamA')}</label>
            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="teamAPlayer1Id"
                render={({ field }) => (
                  <FormItem>
                    <SearchableSelect
                      ref={teamAPlayer1Ref}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        // Auto-advance to next player
                        setTimeout(() => teamAPlayer2Ref.current?.open(), 100);
                      }}
                      options={getAvailablePlayers(field.value).map(p => ({
                        label: p.name,
                        value: p.id,
                        icon: followedPlayers.has(p.id) ? Star : undefined
                      }))}
                      placeholder={t('matches.create.leftPlayer')}
                      searchPlaceholder={t('matches.create.searchPlayer')}
                      emptyMessage={t('matches.create.noOption')}
                      testId="select-team-a-player-1"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="teamAPlayer2Id"
                render={({ field }) => (
                  <FormItem>
                    <SearchableSelect
                      ref={teamAPlayer2Ref}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        // Auto-advance to next player
                        setTimeout(() => teamBPlayer1Ref.current?.open(), 100);
                      }}
                      options={getAvailablePlayers(field.value).map(p => ({
                        label: p.name,
                        value: p.id,
                        icon: followedPlayers.has(p.id) ? Star : undefined
                      }))}
                      placeholder={t('matches.create.rightPlayer')}
                      searchPlaceholder={t('matches.create.searchPlayer')}
                      emptyMessage={t('matches.create.noOption')}
                      testId="select-team-a-player-2"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Team B Selection */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">{t('matches.create.teamB')}</label>
            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="teamBPlayer1Id"
                render={({ field }) => (
                  <FormItem>
                    <SearchableSelect
                      ref={teamBPlayer1Ref}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        // Auto-advance to next player
                        setTimeout(() => teamBPlayer2Ref.current?.open(), 100);
                      }}
                      options={getAvailablePlayers(field.value).map(p => ({
                        label: p.name,
                        value: p.id,
                        icon: followedPlayers.has(p.id) ? Star : undefined
                      }))}
                      placeholder={t('matches.create.leftPlayer')}
                      searchPlaceholder={t('matches.create.searchPlayer')}
                      emptyMessage={t('matches.create.noOption')}
                      testId="select-team-b-player-1"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="teamBPlayer2Id"
                render={({ field }) => (
                  <FormItem>
                    <SearchableSelect
                      ref={teamBPlayer2Ref}
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        // Auto-advance to match type
                        setTimeout(() => setMatchTypeOpen(true), 100);
                      }}
                      options={getAvailablePlayers(field.value).map(p => ({
                        label: p.name,
                        value: p.id,
                        icon: followedPlayers.has(p.id) ? Star : undefined
                      }))}
                      placeholder={t('matches.create.rightPlayer')}
                      searchPlaceholder={t('matches.create.searchPlayer')}
                      emptyMessage={t('matches.create.noOption')}
                      testId="select-team-b-player-2"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Match Settings */}
          <FormField
            control={form.control}
            name="matchType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('matches.create.matchType')}</FormLabel>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Auto-focus start button
                    setTimeout(() => startButtonRef.current?.focus(), 100);
                  }}
                  value={field.value}
                  open={matchTypeOpen}
                  onOpenChange={setMatchTypeOpen}
                >
                  <FormControl>
                    <SelectTrigger data-testid="select-match-type">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="single_set_6">{t('matches.create.types.single_set_6')}</SelectItem>
                    <SelectItem value="single_set_9">{t('matches.create.types.single_set_9')}</SelectItem>
                    <SelectItem value="best_of_3">{t('matches.create.types.best_of_3')}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />


          <Button
            ref={startButtonRef}
            type="submit"
            className="w-full bg-padel-green hover:bg-padel-dark-green text-white dark-btn-active font-bold py-6 text-lg shadow-md transition-all active:scale-[0.98]"
            disabled={createMatchMutation.isPending || playersLoading || !isReadyToStart || isViewer}
            data-testid="button-start-match"
            title={isViewer ? "Action non autorisée en mode visiteur" : undefined}
          >
            {isViewer ? (
              "Mode Visiteur (Lecture Seule)"
            ) : createMatchMutation.isPending ? (
              t('matches.create.creating')
            ) : (
              <>
                <Play className="mr-2 h-5 w-5 fill-current" />
                {t('matches.create.start')}
              </>
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
